import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
// import { useDispatch, useSelector } from "react-redux";
import { doc, onSnapshot } from "firebase/firestore";

import { firestore } from "../../firebase/config";
import { BookingType } from "../../redux/bookings/bookings.types";
// import { RootState } from "../../redux/store";
// import {
//   errorBookingDetailsRealTime,
//   loadingBookingDetails,
//   updateBookingDetailsRealTime,
// } from "../../redux/booking-details/bookingDetails.slice";
import Card from "../UI/card/card.component";
import Spinner from "../UI/spinner/spinner.component";
import {
  BackButtonContainer,
  ClickableArea,
  GreenHeader,
  GreenP,
  RedHeader,
  SpreadButtonContainer,
  TextDivider,
} from "../../global.styles";
import BookingStatus from "./booking-status/booking-status.component";
import Addresses from "./addresses.component";
import { defineVehicleType } from "../../util-functions";
import Button from "../UI/button/button.component";
import DriverInfo from "./driver-info.component";
import { cancelBookingRequest } from "../../firebase/bookings";
import Modal from "../UI/modal/modal.component";
import TollDetails from "./tollDetails.component";

const BookingDetails = () => {
  const navigate = useNavigate();
  // const dispatch = useDispatch();

  const { bookingId } = useParams();

  // //Can't find the reason of keeping this state global
  // const { bookingDetailsLoading, bookingDetailsError, bookingDetails } =
  //   useSelector((state: RootState) => state.bookingDetails);

  const [bookingDetailsLoading, setBookingDetailsLoading] = useState(true);
  const [bookingDetailsError, setBookingDetailsError] = useState("");
  const [bookingDetails, setBookingDetails] = useState<BookingType | null>(
    null
  );
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [bookingCancelled, setBookingCancelled] = useState(false);

  //functions to handle booking document
  const constructBooking = (docId: string, docData: any): BookingType => ({
    id: docId,
    ...docData,
    createdAt: docData.createdAt.toMillis(),
    acceptedAt: docData.acceptedAt?.toMillis(),
    arrivedToPickupAt: docData.arrivedToPickupAt?.toMillis(),
    pickedUpAt: docData.pickedUpAt?.toMillis(),
    completedAt: docData.completedAt?.toMillis(),
    pickupGeoPoint: docData.pickupGeoPoint
      ? {
          latitude: docData.pickupGeoPoint.latitude,
          longitude: docData.pickupGeoPoint.longitude,
        }
      : null,
  });
  const handleBookingDoc = useCallback(
    async (bookingId: string, bookingDoc: any) => {
      if (bookingDoc.exists()) {
        const updatedBooking = constructBooking(bookingId, bookingDoc.data());
        setBookingDetails(updatedBooking);
        setBookingDetailsLoading(false);
      } else {
        setBookingDetailsError("Booking not found");
        setBookingDetailsLoading(false);
      }
    },
    []
  ); // Add dependencies if needed
  const handleError = (error: any, defaultMessage: string) => {
    const errorMessage =
      error instanceof Error ? error.message : defaultMessage;
    setBookingDetailsError(errorMessage);
    setBookingDetailsLoading(false);
  };

  //set onSnapshot (real time updates connection) for this booking
  useEffect(() => {
    if (bookingId) {
      const unsubscribe = onSnapshot(
        doc(firestore, "bookings", bookingId),
        { includeMetadataChanges: true },
        async (bookingDoc) => {
          try {
            if (bookingDoc.exists()) {
              await handleBookingDoc(bookingDoc.id, bookingDoc); // Process the document
            } else {
              setBookingDetailsError("Booking not found");
              setBookingDetailsLoading(false);
            }
          } catch (error) {
            handleError(error, "Error fetching booking details");
          }
        },
        (error) => handleError(error, "Error fetching booking details")
      );

      return () => unsubscribe();
    }
  }, [bookingId, handleBookingDoc]);
  //opening modal and firing action if confirmed
  const cancelBookingHandler = () => {
    setOpenCancelModal(true);
  };
  const cancelBookingConfirmed = async () => {
    if (bookingId) {
      setBookingDetailsLoading(true);
      try {
        const data = await cancelBookingRequest(bookingId);
        if (data && data.message && data.message === "Booking cancelled!") {
          setBookingCancelled(true);
        }
      } catch (error) {
        if (error instanceof Error) {
          setBookingDetailsError(error.message);
        } else {
          setBookingDetailsError(
            "Problem cancelling this booking... Please try again. If problem still exists, please let us know"
          );
        }
      }
      setBookingDetailsLoading(false);
    } else {
      navigate("/bookings");
    }
  };

  if (bookingCancelled) {
    return (
      <Card>
        <GreenHeader>Booking cancelled.</GreenHeader>
        <BackButtonContainer>
          <Button
            buttonType="green"
            onClick={() => {
              navigate("/bookings");
            }}
          >
            OK
          </Button>
        </BackButtonContainer>
      </Card>
    );
  }

  return (
    <Card>
      {openCancelModal && (
        <Modal
          title="Cancel Booking"
          body="Cancel this booking? Are you sure?"
          confirmButtonText="Yes, Cancel"
          setOpenModal={setOpenCancelModal}
          onModalConfirm={cancelBookingConfirmed}
        />
      )}

      {bookingDetailsLoading && <Spinner />}
      {bookingDetailsError && (
        <>
          <RedHeader>{bookingDetailsError}</RedHeader>
          <BackButtonContainer>
            <Button onClick={() => navigate("/bookings")}>Ok</Button>
          </BackButtonContainer>
        </>
      )}
      {!bookingDetailsError && !bookingDetailsLoading && !bookingDetails && (
        <Card>
          <RedHeader>Wrong Parameters...</RedHeader>
          <Button onClick={() => navigate("/bookings")}>Try Again</Button>
        </Card>
      )}
      {bookingDetails && !bookingDetailsLoading && !bookingDetailsError && (
        <>
          <BackButtonContainer>
            <ClickableArea onClick={() => navigate(-1)}>
              <h3>Go Back</h3>
            </ClickableArea>
          </BackButtonContainer>
          <GreenHeader>
            Booking on {bookingDetails?.date} at {bookingDetails?.time}
          </GreenHeader>
          <BookingStatus booking={bookingDetails} />
          {!bookingDetails.publicBooking && bookingDetails.reasonOfReject && (
            <>
              <TextDivider>
                <GreenP> REASON OF REJECT </GreenP>
              </TextDivider>
              <GreenP>
                <strong>{bookingDetails.reasonOfReject}</strong>
              </GreenP>
            </>
          )}
          {bookingDetails.noShowReason && (
            <>
              <TextDivider>
                <GreenP> NO SHOW REASON </GreenP>
              </TextDivider>
              <GreenP>
                <strong>{bookingDetails.noShowReason}</strong>
              </GreenP>
            </>
          )}
          <TextDivider>
            <GreenP> DRIVER INFO </GreenP>
          </TextDivider>
          {bookingDetails.driverNumber && !bookingDetails.publicBooking && (
            <GreenP>
              <strong>
                Private request to Driver {bookingDetails.driverNumber}
              </strong>
            </GreenP>
          )}
          {bookingDetails.accepted && bookingDetails.acceptedBy ? (
            <DriverInfo driver={bookingDetails.acceptedBy} />
          ) : (
            <GreenP>
              Driver Info is not available untill Driver accepts the booking...
            </GreenP>
          )}
          <TextDivider>
            <GreenP> ADDRESSES </GreenP>
          </TextDivider>
          <Addresses booking={bookingDetails} />
          <TextDivider>
            <GreenP> DETAILS </GreenP>
          </TextDivider>
          <GreenP>Passenger Name</GreenP>
          <p>{bookingDetails.passengerName}</p>
          <br />
          <GreenP>Passenger Phone Number</GreenP>
          <p>{bookingDetails.passengerPhone}</p>
          <br />
          <GreenP>Vehicle Type:</GreenP>
          <p>{defineVehicleType(bookingDetails.vehicleType)}</p>
          {bookingDetails.childSeat && (
            <>
              <br />
              <GreenP>Child Seat</GreenP>
              <p>Yes</p>
            </>
          )}
          {bookingDetails.hasPet && (
            <>
              <br />
              <GreenP>Pet Friendly</GreenP>
              <p>Yes</p>
            </>
          )}
          {bookingDetails.meetAndGreet && (
            <>
              <br />
              <GreenP>Meet & Greet requested</GreenP>
              <p>Yes</p>
            </>
          )}
          {bookingDetails.driverNote && (
            <>
              <br />
              <GreenP>Note to the Driver</GreenP>
              <p>{bookingDetails.driverNote}</p>
            </>
          )}
          <TextDivider>
            <GreenP> PRICE </GreenP>
          </TextDivider>
          <GreenHeader> £{bookingDetails.quotedPrice}</GreenHeader>
          <TollDetails booking={bookingDetails} />
          {!bookingDetails.completed && (
            <>
              <TextDivider>
                <GreenP> ACTIONS </GreenP>
              </TextDivider>
              <SpreadButtonContainer>
                {/* <Button buttonType="inverted" onClick={() => {}}>
                  Edit
                </Button> */}
                <Button buttonType="warning" onClick={cancelBookingHandler}>
                  Cancel Booking
                </Button>
              </SpreadButtonContainer>
            </>
          )}
        </>
      )}
    </Card>
  );
};

export default BookingDetails;
